import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { PageInfoGoodsSkuPageVO } from '@app/api/goods-service/model/pageInfoGoodsSkuPageVO';
import { GoodsSkuPageVO } from '@app/api/goods-service/model/goodsSkuPageVO';
import { HttpResponse } from '@angular/common/http';
import { ProductItem } from '../components/product-selection-dialog/product-selection-dialog.component';
import {
  getPackageFulfillmentModeLabel,
  PRODUCT_SERVICE_CONFIG,
} from '../internal.config';

type ProductSearchResult = GoodsSkuPageVO & {
  brandName?: string;
};

export interface ProductSearchParams {
  spuName?: string;
  spuCode?: string;
  skuCode?: string;
  skuName?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private goodsSkuService = inject(GoodsSkuControllerService);

  // Configuration
  private config = PRODUCT_SERVICE_CONFIG;

  // State management
  private productsSubject = new BehaviorSubject<ProductItem[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private totalRecordsSubject = new BehaviorSubject<number>(0);

  // Public observables
  products$ = this.productsSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  totalRecords$ = this.totalRecordsSubject.asObservable();

  /**
   * Search products with pagination and filters
   */
  searchProducts(params: ProductSearchParams): Observable<{ products: ProductItem[], totalRecords: number }> {
    this.loadingSubject.next(true);

    const searchParams = {
      page: (params.page || 1) - 1, // Convert to 0-based
      size: params.size || this.config.defaultPageSize,
      ...(params.spuName && { spuName: params.spuName }),
      ...(params.spuCode && { spuCode: params.spuCode }),
      ...(params.skuCode && { skuCode: params.skuCode }),
      ...(params.skuName && { skuName: params.skuName }),
      status: this.config.statusFilter,
      sortBy: params.sortBy || this.config.defaultSortBy,
      ascending: params.ascending !== undefined ? params.ascending : this.config.defaultAscending
    };

    return new Observable(observer => {
      this.goodsSkuService.list9(
        undefined, // keyword
        searchParams.spuName,
        searchParams.spuCode,
        undefined, // spuType
        undefined, // setFlag
        undefined, // categoryIdList
        searchParams.status,
        searchParams.skuName,
        undefined, // searchMode
        searchParams.skuCode,
        undefined, // startDate
        undefined, // endDate
        undefined, // skuIds
        undefined, // lastUpdatedAfter
        undefined, // channelShopId
        undefined, // warehouseCode
        1, // showFlag - only Active SKUs can be added to a new internal order
        searchParams.page,
        searchParams.size,
        searchParams.sortBy,
        searchParams.ascending,
        'response',
        false,
        {
          httpHeaderAccept: 'application/json' as any
        }
      ).subscribe({
        next: (response: HttpResponse<PageInfoGoodsSkuPageVO>) => {

          if (response?.body?.pageContent) {
            const productItems: ProductItem[] = response.body.pageContent.map((item: ProductSearchResult) => {
              // Calculate display price with priority: clearingPrice > activityPrice > salesPrice
              const clearingPrice = item.clearingPrice || 0;
              const activityPrice = item.activityPrice || 0;
              const salesPrice = item.salesPrice || 0;

              let displayPrice = salesPrice; // Default to salesPrice
              if (clearingPrice > 0) {
                displayPrice = clearingPrice;
              } else if (activityPrice > 0) {
                displayPrice = activityPrice;
              }

              return {
                id: item.id ?? 0,
                spuName: item.spuName || '',
                spuCode: item.spuCode || '',
                skuName: item.skuName || '',
                skuCode: item.skuCode || '',
                brandName: item.brandName || '',
                categoryName: item.categoryName || '',
                salesPrice: salesPrice,
                costPrice: item.costPrice || 0,
                activityPrice: activityPrice,
                clearingPrice: clearingPrice,
                displayPrice: displayPrice,
                spuId: item.spuId ?? 0,
                skuId: item.id ?? 0,
                setFlag: item.setFlag ?? 0,
                isPackage: item.isPackage ?? item.setFlag === 1,
                fulfillmentMode: item.fulfillmentMode,
                fulfillmentModeLabel: getPackageFulfillmentModeLabel(
                  item.isPackage ?? item.setFlag === 1,
                  item.fulfillmentMode,
                ),
              };
            });

            this.productsSubject.next(productItems);
            this.totalRecordsSubject.next(response.body.totalElements || 0);

            observer.next({
              products: productItems,
              totalRecords: response.body.totalElements || 0
            });

          } else {
            this.productsSubject.next([]);
            this.totalRecordsSubject.next(0);
            observer.next({ products: [], totalRecords: 0 });
          }

          this.loadingSubject.next(false);
          observer.complete();
        },
        error: (error) => {
          console.error('ProductService: Error loading products:', error);
          this.productsSubject.next([]);
          this.totalRecordsSubject.next(0);
          this.loadingSubject.next(false);
          observer.error(error);
        }
      });
    });
  }

  /**
   * Get current products
   */
  getCurrentProducts(): ProductItem[] {
    return this.productsSubject.value;
  }

  /**
   * Get current loading state
   */
  getCurrentLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Get current total records
   */
  getCurrentTotalRecords(): number {
    return this.totalRecordsSubject.value;
  }

  /**
   * Clear products
   */
  clearProducts(): void {
    this.productsSubject.next([]);
    this.totalRecordsSubject.next(0);
  }
}
